/**
 * Calendar event-type categories — the store behind Settings → Calendar.
 * Admins rename / recolour built-in categories and add custom ones; the
 * Week / Month / Day views recolour from this store. Persisted to
 * localStorage (interim — same pattern as the other admin-authored stores).
 *
 * Built-in categories can be renamed and recoloured but NOT deleted — seed
 * calendar events carry colours that map onto built-in typeIds via
 * `classifyEventColor`, and deleting them would orphan existing rows.
 */
import { useEffect, useState } from "react";
import { listCalendarCategories, replaceCalendarCategories } from "@v0y4ge/api-client";
import { can, isSignedIn, onTenantChange, onTenantSwitching } from "../../shell/access";

export interface CalCategory { id: string; name: string; color: string; builtIn: boolean; matchColors: string[]; }
export interface PaletteColor { name: string; hex: string; }

/* Curated on-brand tertiary palette — no free hex picker (F-08). */
export const CAL_CATEGORY_PALETTE: PaletteColor[] = [ // ui-config — curated brand color palette, not tenant data
  { name: "Purple",     hex: "#8955F2" },
  { name: "Light blue", hex: "#75ADFA" },
  { name: "Dark blue",  hex: "#4981F2" },
  { name: "Turquoise",  hex: "#5AC8AD" },
  { name: "Green",      hex: "#87D23B" },
  { name: "Yellow",     hex: "#F5C61E" },
  { name: "Orange",     hex: "#EE9D4F" },
  { name: "Red",        hex: "#F25589" },
  { name: "Pink",       hex: "#C18FE8" },
];

/* Built-ins — each carries the seed-event colours it claims (matchColors). */
export const BUILT_IN_CAL_CATEGORIES: CalCategory[] = [ // ui-config — fixed product catalog of built-in calendar types (tenant-agnostic); tenant overrides hydrate from listCalendarCategories
  { id: "flagship",  name: "Flagship events",   color: "#8955F2", builtIn: true, matchColors: ["#8955F2", "#5142C7"] },
  { id: "workshops", name: "Workshops & labs",  color: "#5AC8AD", builtIn: true, matchColors: ["#5AC8AD", "#87D23B"] },
  { id: "vip",       name: "VIP & private",     color: "#C18FE8", builtIn: true, matchColors: ["#C18FE8", "#F25589"] },
  { id: "community", name: "Community",         color: "#75ADFA", builtIn: true, matchColors: ["#75ADFA", "#4981F2"] },
  { id: "popup",     name: "Pop-ups & retail",  color: "#EE9D4F", builtIn: true, matchColors: ["#EE9D4F", "#F5C61E"] },
];

/**
 * 2026-07-03 (Claude Code push-back, §2 — F-08): `listCalendarCategories` /
 * `replaceCalendarCategories` are live over the real `/v1/admin/settings/
 * calendar-categories` route. This store now hydrates from + persists
 * through that client instead of localStorage. The in-memory built-ins
 * below are kept ONLY as a same-session fallback for when the client
 * operations aren't present (this authoring preview's fixture stub doesn't
 * implement them yet); a successful hydrate() fully replaces them.
 */
let _cats: CalCategory[] = BUILT_IN_CAL_CATEGORIES.map((c) => ({ ...c, matchColors: [...c.matchColors] }));
const subs = new Set<() => void>();
const emit = () => { subs.forEach((fn) => fn()); };

function fromDto(c: Record<string, any>): CalCategory {
  return { id: c.id, name: c.name ?? "", color: c.color ?? "#5142C7", builtIn: !!c.builtIn, matchColors: c.matchColors ?? [] };
}

let _hydrated = false;
async function hydrate() {
  // §2 boot hygiene (2026-07-19): no fetch before a session exists — skipping
  // keeps the flag clear so subscribeCalCategories re-invokes post-sign-in.
  if (_hydrated || !isSignedIn()) return;
  _hydrated = true;
  try {
    if (typeof listCalendarCategories !== "function") return;
    const r: any = await listCalendarCategories({});
    const arr = r?.categories;
    if (Array.isArray(arr) && arr.length) { _cats = arr.map(fromDto); emit(); }
  } catch (e) { console.warn("Calendar categories hydrate failed, keeping built-in fallback:", e); }
}
hydrate(); // no-op until signed in (§2)
onTenantSwitching(() => { _cats = BUILT_IN_CAL_CATEGORIES.map((c) => ({ ...c, matchColors: [...c.matchColors] })); _hydrated = false; emit(); });
// Capability gate (docket 2026-07-31): pure over-fetch for most personas — the
// only consumer of this store is SettingsCalendar (an Operations Admin surface);
// no Events or Calendar screen reads categories or their colours. `settings.read`
// is the cap the sibling settings routes use (settings.calendar itself has no
// ROUTE_READ_PERM entry, so there is no narrower one to borrow, and no new
// capability needs to reach anybody). subscribeCalCategories() still hydrates on
// demand, so the settings screen is unaffected either way.
onTenantChange(() => { if (can("settings.read")) hydrate(); });

/** Persist to the calendar-categories contract op. Returns the op's promise so
 *  callers can await before declaring success (no-fake-interactivity mandate). */
function persist(next: CalCategory[]): Promise<unknown> {
  if (typeof replaceCalendarCategories === "function") {
    return Promise.resolve(replaceCalendarCategories(next));
  }
  return Promise.resolve();
}

export function getCalCategories(): CalCategory[] { return _cats.map((c) => ({ ...c, matchColors: [...(c.matchColors || [])] })); }
export function setCalCategories(next: CalCategory[]): Promise<unknown> { _cats = next.map((c) => ({ ...c, matchColors: [...(c.matchColors || [])] })); emit(); return persist(_cats); }
export function resetCalCategories(): CalCategory[] { _cats = BUILT_IN_CAL_CATEGORIES.map((c) => ({ ...c, matchColors: [...c.matchColors] })); emit(); persist(_cats).catch((e: unknown) => console.warn("replaceCalendarCategories failed:", e)); return getCalCategories(); }
export function subscribeCalCategories(fn: () => void) { hydrate(); subs.add(fn); return () => { subs.delete(fn); }; }
export function useCalCategories(): CalCategory[] {
  const [, force] = useState(0);
  useEffect(() => subscribeCalCategories(() => force((n) => n + 1)), []);
  return getCalCategories();
}

/** Map an event's raw colour to its category (built-ins claim seed colours). */
export function classifyEventColor(color: string | undefined, cats: CalCategory[] = _cats): { typeId: string; color: string } | null {
  if (!color) return null;
  const up = color.toUpperCase();
  const hit = cats.find((c) => (c.matchColors || []).some((m) => m.toUpperCase() === up) || c.color.toUpperCase() === up);
  return hit ? { typeId: hit.id, color: hit.color } : null;
}
